import React, { useContext, useState } from "react";
import Button from "Components/button/Button";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import { addDomainAlias, plus, trash } from "Icons/index";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import TranslatedString from "Components/TranslatedString";
import t from "Utils/translation";
import qs from "query-string";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import s from "./listAliasDomains.scss";
import { AuthContext, MediaQueriesContext } from "../../App";
import { useApiMutation, useApiQuery } from "../../hooks";
import { useHistory, useLocation } from "react-router-dom";

const AliasDomainsListScreen = ({ match }) => {
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [selectedAliasDomain, setSelectedAliasDomain] = useState({});

  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);

  const history = useHistory();
  const location = useLocation();

  const {
    data: aliasDomains,
    isLoading: areAliasDomainsLoading,
    isRefreshing: isRefreshingAliasDomains,
    refreshData: refreshAliasDomains,
    getData: fetchAliasDomains,
    pagination: aliasDomainsPaginationInfo,
  } = useApiQuery("getDomainsAliasDomains", {
    enablePagination: true,
    requestParams: match.params.domainCode,
    autoRefresh: {
      shouldRefresh: (data) => data.some((ad) => ad.status === "updating"),
      interval: 4000,
    },
  });
  const { mutate: deleteAliasDomain } = useApiMutation("deleteAliasDomain");

  const closeDeleteConfirmationModal = () => {
    setConfirmModalOpened(false);
    setSelectedAliasDomain({});
  };

  const openDeleteConfirmationModal = (domain) => {
    setConfirmModalOpened(true);
    setSelectedAliasDomain(domain);
  };

  const navigateToAddAliasDomain = () => {
    if (isLoggedUserAdmin()) {
      history.push(`/customers/${match.params.customerCode}/domains/${match.params.domainCode}/aliases/add`);
    } else {
      history.push(`/domains/${match.params.domainCode}/aliases/add`);
    }
  };

  const navigateToOwnershipCheck = (code) => {
    history.push({
      pathname: `/domains/${match.params.domainCode}/aliases/${code}/ownership_check`,
      state: {
        prevPagination: location.search,
      },
    });
  };

  const handleDeleteAliasDomain = async () => {
    try {
      const aliasCode = selectedAliasDomain.code;
      await deleteAliasDomain(match.params.domainCode, aliasCode);
      refreshAliasDomains();
      closeDeleteConfirmationModal();
    } catch (error) {
      console.error(error);
    }
  };

  const canAddDomain = getLoggedUser().type !== "TeamMember" || getLoggedUser().permissions.includes("create_domains");

  const query = qs.parse(location.search);

  return (
    <div className={s.listAliasDomains}>
      <div className={s.wrapInfoBtn}>
        {!isMediaMobile && (
          <div className={s.infoText}>
            <TranslatedString>domains.show.desc.aliasDomains</TranslatedString>
          </div>
        )}

        {canAddDomain && (
          <Button className={s.addBtn} icon={plus} onClick={navigateToAddAliasDomain}>
            button.addAlias
          </Button>
        )}
      </div>
      <FullPaginationControls
        className={s.filters}
        paginationInfo={aliasDomainsPaginationInfo}
        location={location}
        refreshList={() => fetchAliasDomains(match.params.domainCode)}
        showListFilter={false}
        isLoading={areAliasDomainsLoading || isRefreshingAliasDomains}
      />

      <div className={s.table}>
        <ResponsiveTable
          history={history}
          areRowsLoading={areAliasDomainsLoading && !isRefreshingAliasDomains}
          location={location}
          addIcon={addDomainAlias}
          addMsg={t("table.row.add.msg.domainAlias")}
          addDesc={t("table.row.add.desc.domainAlias")}
          headers={[
            {
              name: t("table.header.status"),
              size: isMobileDevice ? "2rem" : "4rem",
              center: true,
            },
            {
              name: t("table.header.alias"),
              key: "name",
              sortable: true,
              activeSorting: query.sort_field === "name" && query.sort,
            },
            { name: t("table.header.code"), size: "6rem", hideOnMobile: true },
            { size: "3rem", center: true },
          ]}
          rows={aliasDomains.map((em) => {
            return (
              <tr key={em.code}>
                <td data-label={t("table.header.status")}>
                  <DomainStatusBadge status={em.status} isHealthy statusDetail={em.status_detail} />
                </td>
                <td data-label={t("table.header.alias")}>
                  {em.status === "ownership_check" ? (
                    <span className="internalLink" onClick={() => navigateToOwnershipCheck(em.code)}>
                      {em.name}
                    </span>
                  ) : (
                    em.name
                  )}
                </td>
                <td data-label={t("table.header.code")} className="hideOnMobile">
                  {em.code}
                </td>
                <td className="center">
                  <ButtonIcon icon={trash} onClick={() => openDeleteConfirmationModal(em)} />
                </td>
              </tr>
            );
          })}
        />
      </div>
      <ConfirmModal
        title={t("confirmationModal.title.delete.domainAlias")}
        text={
          <TranslatedString subs={{ domainAliasName: selectedAliasDomain.name }}>
            confirmationModal.text.delete.domainAlias
          </TranslatedString>
        }
        textButton={`button.delete`}
        opened={confirmModalOpened}
        action={handleDeleteAliasDomain}
        handleClose={closeDeleteConfirmationModal}
      />
    </div>
  );
};

export default AliasDomainsListScreen;
